import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import editaisAdminService from "../../../services/editaisAdminService";
import type { Edital } from "../../../services/editaisAdminService";

const STATUS_COLORS = {
  rascunho: "bg-gray-100 text-gray-800",
  publicado: "bg-green-100 text-green-800",
  encerrado: "bg-red-100 text-red-800",
};

const STATUS_LABELS = {
  rascunho: "Rascunho",
  publicado: "Publicado",
  encerrado: "Encerrado",
};

export default function ListaEditais() {
  const [editais, setEditais] = useState<Edital[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string>("");

  const carregarEditais = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = filtroStatus ? { status: filtroStatus } : undefined;
      const response = await editaisAdminService.listar(params);

      setEditais(response.editais);
    } catch (err) {
      setError("Erro ao carregar editais");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarEditais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus]);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Editais</h1>
          <p className="mt-2 text-gray-600">Gerencie os editais publicados</p>
        </div>
        <Link
          to="/admin/editais/novo"
          className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition font-medium"
        >
          + Novo Edital
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Status:</label>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Todos</option>
            <option value="rascunho">Rascunho</option>
            <option value="publicado">Publicado</option>
            <option value="encerrado">Encerrado</option>
          </select>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando editais...</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Lista */}
      {!loading && !error && (
        <>
          {editais.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-12 text-center">
              <p className="text-gray-600">Nenhum edital encontrado.</p>
              <Link
                to="/admin/editais/novo"
                className="mt-4 inline-block text-blue-600 hover:text-blue-800"
              >
                Criar primeiro edital
              </Link>
            </div>
          ) : (
            <div className="grid gap-4">
              {editais.map((edital) => (
                <div
                  key={edital.id}
                  className="bg-white rounded-lg shadow hover:shadow-md transition p-6"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">
                          {edital.nome}
                        </h3>
                        <span
                          className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            STATUS_COLORS[edital.status]
                          }`}
                        >
                          {STATUS_LABELS[edital.status]}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {edital.descricao}
                      </p>
                      <div className="flex items-center gap-6 text-sm text-gray-500">
                        <div>
                          <span className="font-medium">Início:</span>{" "}
                          {new Date(edital.data_inicio).toLocaleDateString(
                            "pt-BR"
                          )}
                        </div>
                        <div>
                          <span className="font-medium">Fim:</span>{" "}
                          {new Date(edital.data_fim).toLocaleDateString(
                            "pt-BR"
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Link
                        to={`/admin/editais/${edital.id}`}
                        className="text-blue-600 hover:text-blue-800 px-4 py-2 border border-blue-600 rounded-md hover:bg-blue-50 transition"
                      >
                        Ver Detalhes
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
